import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt } from '../utils/format';
import Pager from '../components/Pager';
import Alert from '../components/Alert';

const PAGE_SIZE = 10;

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Client-side pagination state
  const [topPage, setTopPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);

  useEffect(() => {
    setLoading(true);
    setTopPage(1);
    setInvoicePage(1);
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    api.get('/reports', { params })
      .then(res => setData(res.data))
      .catch(() => setError('Could not load reports.'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const rangeActive = dateFrom || dateTo;

  // Paginate in-memory arrays
  const topProducts = data?.topProducts ?? [];
  const topSlice = topProducts.slice((topPage - 1) * PAGE_SIZE, topPage * PAGE_SIZE);
  const topPages = Math.ceil(topProducts.length / PAGE_SIZE);

  const invoices = data?.invoices ?? [];
  const invoiceSlice = invoices.slice((invoicePage - 1) * PAGE_SIZE, invoicePage * PAGE_SIZE);
  const invoicePages = Math.ceil(invoices.length / PAGE_SIZE);

  return (
    <Layout title="Reports & Analytics">
      <Alert type="danger" message={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From date</label>
          <input type="date" value={dateFrom} max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">To date</label>
          <input type="date" value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} />
        </div>
        {rangeActive && (
          <button className="btn" onClick={() => { setDateFrom(''); setDateTo(''); }}>
            <i className="ti ti-x" aria-hidden="true"></i>Clear
          </button>
        )}
        <div style={{ fontSize: 12, color: 'var(--muted, #777)', paddingBottom: 6 }}>
          {rangeActive ? `Showing: ${dateFrom || 'earliest'} → ${dateTo || 'latest'}` : 'Showing: Today'}
        </div>
      </div>

      {loading ? (
        <div className="empty-state">Loading reports...</div>
      ) : data && (
        <>
          <div className="metric-grid">
            <div className="metric">
              <div className="metric-label">Total Revenue</div>
              <div className="metric-value" style={{ fontSize: 16 }}>
                ₹{Number(data.totalRevenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="metric blue">
              <div className="metric-label">Total Invoices</div>
              <div className="metric-value">{data.invoiceCount}</div>
            </div>
            <div className="metric orange">
              <div className="metric-label">Avg Invoice</div>
              <div className="metric-value" style={{ fontSize: 16 }}>
                ₹{Number(data.avgInvoice).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="metric">
              <div className="metric-label">Products</div>
              <div className="metric-value">{data.totalProducts}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Top Selling Products */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Top Selling Products</div>
                {topProducts.length > 0 && (
                  <span style={{ fontSize: 11, color: '#9e9e9e' }}>{topProducts.length} products</span>
                )}
              </div>
              {topProducts.length === 0 ? (
                <div className="empty-state" style={{ padding: '1rem' }}>No data for this period</div>
              ) : (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Units Sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSlice.map((p, i) => (
                        <tr key={i}>
                          <td>{(topPage - 1) * PAGE_SIZE + i + 1}</td>
                          <td style={{ fontSize: 11 }}>{p.product_name}</td>
                          <td><strong>{p.units_sold}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pager page={topPage} pages={topPages} total={topProducts.length} onPage={setTopPage} label="products" />
                </>
              )}
            </div>

            {/* Invoice Summary */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Invoice Summary</div>
                {invoices.length > 0 && (
                  <span style={{ fontSize: 11, color: '#9e9e9e' }}>{invoices.length} invoices</span>
                )}
              </div>
              {invoices.length === 0 ? (
                <div className="empty-state" style={{ padding: '1rem' }}>No invoices for this period</div>
              ) : (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Customer</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceSlice.map(i => (
                        <tr key={i.id}>
                          <td style={{ color: 'var(--green)', fontWeight: 600 }}>{i.invoice_number}</td>
                          <td style={{ fontSize: 11 }}>{i.customer_name || 'Walk-in'}</td>
                          <td>{fmt(i.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pager page={invoicePage} pages={invoicePages} total={invoices.length} onPage={setInvoicePage} label="invoices" />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
